import React, { useEffect, useState, useRef, useContext, useMemo } from 'react';
import { FieldData, FieldOption, MultiSearchSelectFieldType } from 'types/FieldTypes';
import { useTranslation, Trans } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useClickOutside, useSetDropdownWidth, calculateDropdownPosition } from 'services/utils/dropdownBehavior'
import { useFetchSearchHook } from 'services/api/fetchSearchHook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import FormFieldContext from '../FormFieldContext';
import '../../../style/scss/forms.scss';

interface MultiSearchSelectProps extends MultiSearchSelectFieldType {
    updatedData?: any,
    selectedOptions: FieldOption[],
    onAdd: (option: FieldOption) => void;
    onAddMany?: (options: FieldOption[]) => void; // Adds several options at once, used by bulk created items
    onRemove: (option: FieldOption) => void;
    onPreventClose?: (shouldPreventClose: boolean) => void;
}

const MultiSearchSelect: React.FC<MultiSearchSelectProps & { data?: FieldData }> = ({
    query, updatedData, placeholder, objectName, name, selectionFormat, optionFormat,
    isInvalid, disableAddNew = false, addNewItem, disabled, onPreventClose,
    showOptionsOnClick, directOptionsTitle, specialOptions, selectedOptions, onAdd, onAddMany, onRemove
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { setUpdatedData } = useContext(FormFieldContext);
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [directSearch, setDirectSearch] = useState(false);

    // Resolve the query from the given function or param
    const resolvedQuery = useMemo(() => {
        if (typeof query === 'function') return query(updatedData);
        return query;
    }, [query, updatedData]);

    // Set the query states
    const [activeEndpoint, setActiveEndpoint] = useState(resolvedQuery.endpoint);
    const [activeParams, setActiveParams] = useState(resolvedQuery.params);

    // Get the direct search config, if set
    const directSearchConfig = useMemo(() => {
        if (search !== '') return null;
        if (!showOptionsOnClick || !updatedData) return null;
        return showOptionsOnClick(updatedData);
    }, [search, showOptionsOnClick, updatedData]);

    // Determine if the direct search should be executed when clicking on the field
    const isDirectSearchMode = directSearch && (search === '' && !!directSearchConfig);

    // Fetch the options
    const { options, loading, fetchFinished, resetLastFetchValue } = useFetchSearchHook(activeEndpoint, activeParams, search, isDirectSearchMode);

    // Other states
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownListRef = useRef<HTMLDivElement>(null);
    const fieldContainerRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLInputElement>(null);
    const [dropdownWidth, setDropdownWidth] = useState<number>(200);
    const [dropdownAbove, setDropdownAbove] = useState(false);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>(undefined);

    // Use the active query when entering a search value
    useEffect(() => {
        if (search.length >= 2) {
            setActiveEndpoint(resolvedQuery.endpoint);
            setActiveParams(resolvedQuery.params);
        }
    }, [search, resolvedQuery.endpoint, resolvedQuery.params]);

    // Leave the direct search mode when entering a search value
    useEffect(() => {
        if (search.length >= 2 && directSearch) {
            setDirectSearch(false);
        }
    }, [search, directSearch]);

    // Open the dropdown when a fetch delivered options
    useEffect(() => {
        if (fetchFinished && options.length > 0) {
            openDropdown();
        }
    }, [fetchFinished, options]);

    // Close the dropdown when the search value is made empty
    useEffect(() => {
        if (search === '' && !directSearch) {
            setIsOpen(false);
        }
    }, [search, directSearch]);

    // Helper function that gives the value of an object by its path
    function getValueByPath(obj: any, path: string) {
        const parts = path.split(/[.\[\]]/).filter((part: string) => part.length);
        return parts.reduce((acc: any, part: string) => acc && acc[part], obj);
    }

    // Format the text in the selection list options
    function formatOption(option: any) {
        let title = '';
        let bracket = '';
        let subtitle = '';

        // Format the option by the given option format
        if (optionFormat) {

            // Format the title
            if (optionFormat.title) {
                const { field, format } = optionFormat.title;
                const value = getValueByPath(option, field);
                title = format(value);
            }

            // Format the optional text between the brackets
            if (optionFormat.bracket) {
                const { field, format, optional } = optionFormat.bracket;
                const value = getValueByPath(option, field);
                if (value || !optional) {
                    bracket = `(${format(value)})`;
                }
            }

            // Format the optional subtitle
            if (optionFormat.subtitle) {
                const { field, format, optional } = optionFormat.subtitle;
                const value = getValueByPath(option, field);
                if (value || !optional) {
                    subtitle = format(value);
                }
            }

        // Set the title in case no option format is given
        } else {
            title = option.name || '';
        }

        // Render the format of the option
        return (
            <React.Fragment>
                <div>{title} {bracket}</div>
                {subtitle && <div className='subtitle'>{subtitle}</div>}
            </React.Fragment>
        );
    }

    // Format the text of a selected option which is shown as chip
    function formatSelection(option: FieldOption) {

        // Show the translated label of a selected special option
        const specialOption = specialOptions?.find(special => special.value === option.value);
        if (specialOption) return t(specialOption.label);

        return String(option[selectionFormat] ?? option.name ?? '');
    }

    // Determine whether an option is already selected
    const isSelected = (option: FieldOption) => {
        return selectedOptions.some(selected =>
            (option.id !== undefined && selected.id === option.id) ||
            (option.id === undefined && selected.value === option.value)
        );
    }

    // Filter the already selected options out of the fetched options
    const selectableOptions = options.filter((option: FieldOption) => !isSelected(option));

    // Only offer the special options when there are real objects to select
    const selectableSpecialOptions = (specialOptions || []).filter(special =>
        options.length > 0 &&
        !selectedOptions.some(selected => selected.value === special.value)
    );

    // Determine whether an exact match on the search value exists
    const isExactMatch = () => {
        return options.some((option) => (option.name || '').toLowerCase() === search.toLowerCase());
    };

    // Handle the selection of an option
    const handleSelect = (option: FieldOption) => {
        onAdd(option);
        setSearch('');
        setIsOpen(false);
        setDirectSearch(false);
        resetLastFetchValue();
    };

    // Handle the selection of a special option
    const handleSelectSpecial = (special: { value: string; label: string; dataField?: string }) => {
        handleSelect({ value: special.value, name: t(special.label) });
    };

    // Convert a newly added item to a selectable option
    const convertNewAddedItem = (item: Record<string, any>): FieldOption => (
        { id: item['id'], value: item['id'], name: item[selectionFormat], ...item }
    );

    // Link the newly added item or items as selected options
    const handleLinkNewAddedItem = (response: Record<string, any> | Record<string, any>[]) => {

        // On refetch, stop prevent closing
        if (onPreventClose) onPreventClose(false);

        // Add every item of a bulk response at once so no item is lost
        if (Array.isArray(response)) {
            const newOptions = response.map(convertNewAddedItem);
            if (onAddMany) {
                onAddMany(newOptions);
            } else {
                newOptions.forEach(option => onAdd(option));
            }
            setSearch('');
            setIsOpen(false);
            setDirectSearch(false);
            resetLastFetchValue();
            return;
        }

        // Convert the single newly added item to an option and handle as selected
        handleSelect(convertNewAddedItem(response));
    }

    // Create a new option
    const createNewOption = (newOption: string) => {
        if (addNewItem && addNewItem.method === 'modal-form') {

            // Prevent closing of the field when editing the modal
            if (onPreventClose) {
                onPreventClose(true);
            }

            // Add the new option directly to the modal element if it is a function
            const modalElement = typeof addNewItem.modalForm === 'function' ? addNewItem.modalForm(newOption) : addNewItem.modalForm;

            // Resolve the linked item from the current form data if it is a function
            const resolvedLinkedItem = typeof addNewItem.linkedItem === 'function' ? addNewItem.linkedItem(updatedData) : addNewItem.linkedItem;

            // Open the modal
            initializeModal(
                React.cloneElement(modalElement, {
                    linkedItem: resolvedLinkedItem,
                    searchValue: newOption
                }), {
                    modalSize: 'add-form-smaller',
                    title: t('button.add_object_label', { object_name: t(objectName) }),
                    onSuccess: (response) => {
                        if (!response) return;

                        handleLinkNewAddedItem(response);

                        if (addNewItem?.onSuccess) {
                            addNewItem.onSuccess(response, { setUpdatedData });
                        }
                    }
                }
            );

            // Close the dropdown
            setIsOpen(false);
        }
    }

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        openDropdown();
    };

    // Use dropdownBehavior.ts to close the dropdown when clicking outside
    useClickOutside(isOpen, dropdownRef, setIsOpen);

    // Use dropdownBehavior.ts to set the dropdown width on open
    useSetDropdownWidth(fieldContainerRef, setDropdownWidth);

    // Handle the opening of the dropdown
    const openDropdown = () => {
        if (!fieldContainerRef.current) return;

        // Calculate the dropdown position
        calculateDropdownPosition(fieldContainerRef, setDropdownAbove, setDropdownMaxHeight);

        // Open the dropdown
        setIsOpen(true);
    };

    // Handle a click on the search field
    const handleFieldClick = () => {
        if (disabled) return;

        // Focus the search input inside the field
        fieldRef.current?.focus();

        // Execute direct search on clicking the field
        if (search === '' && directSearchConfig) {
            setActiveEndpoint(directSearchConfig.endpoint);
            setActiveParams(directSearchConfig.params);
            setDirectSearch(true);
        }
    };

    // Determine whether the dropdown holds anything to show
    const hasDropdownContent = selectableOptions.length > 0 || selectableSpecialOptions.length > 0 || (fetchFinished && search.length >= 2);

    return (
        <div className='searchselect-container multi-searchselect-container'>
            <div className={`custom-dropdown-field ${isInvalid ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
                 onClick={handleFieldClick}
                 ref={fieldContainerRef}>
                <div className='multiselect-selected-items'>
                    {selectedOptions.map((option, index) => (
                        <div key={String(option.id ?? option.value ?? index)}
                             className='multiselect-selected-item'
                             onClick={(event) => event.stopPropagation()}>
                            {formatSelection(option)}
                            {!disabled && (
                                <span className='multiselect-item-remove'
                                      onClick={() => onRemove(option)}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </span>
                            )}
                        </div>
                    ))}
                    <input
                        type='text'
                        name={name}
                        ref={fieldRef}
                        value={search}
                        onChange={handleInputChange}
                        className='multiselect-search-input'
                        placeholder={selectedOptions.length === 0 ? t(placeholder) : ''}
                        disabled={disabled}
                        autoComplete="new-password"
                        data-lpignore="true"
                        data-1p-ignore
                    />
                </div>
            </div>
            {loading && <div className="loader"></div>}
            {isOpen && !disabled && !loading && hasDropdownContent && (
                <div className={`custom-dropdown ${dropdownAbove ? 'dropdown-above' : ''}`} ref={dropdownRef} style={{width: (dropdownWidth - 2), maxHeight: dropdownMaxHeight}}>
                    <div className='custom-dropdown-list' style={{width: dropdownWidth}}>
                        <div className='searchselect-options' ref={dropdownListRef}>

                            {isDirectSearchMode && directOptionsTitle && (
                                <div className="custom-dropdown-title">
                                    {t(directOptionsTitle)}
                                </div>
                            )}

                            {selectableOptions.map((option: FieldOption) => (
                                <div key={option.id || option.name}
                                     className='custom-dropdown-item'
                                     onClick={() => handleSelect(option)}>
                                        {formatOption(option)}
                                </div>
                            ))}

                            {selectableSpecialOptions.map(special => (
                                <div key={special.value}
                                     className='custom-dropdown-item'
                                     onClick={() => handleSelectSpecial(special)}>
                                    <div>{t(special.label)}</div>
                                </div>
                            ))}

                            {selectableOptions.length === 0 && selectableSpecialOptions.length === 0 && (
                                <div className='custom-dropdown-item-no-options'>
                                    {t('forms.no_results_found')}
                                </div>
                            )}

                            {options.length >= 50 &&
                                <div className="limit-message">
                                    {t('forms.dropdown_option_limit_message')}
                                </div>
                            }
                        </div>
                    </div>
                    {search.length >= 2 && !isExactMatch() && fetchFinished && !disableAddNew && !isDirectSearchMode && addNewItem && (
                        <div className='searchselect-add' onClick={() => createNewOption(search)}>
                            <div className='add-label'>
                                <Trans
                                    i18nKey="forms.searchselect_add_new_label"
                                    values={{ search: search, objectName: t(objectName) }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSearchSelect;
